import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
    formatTooltipData,
} from "./utils/filterChartData";

export function DebtComposition({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const filteredData = filterNullValuesFromChartData(data || []);
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Debt Composition"
            description="Total debt has consistently remained very high relative to operating performance, and rising debt-to-equity imbalances paired with reconciliation discrepancies and rapidly declining outstanding balances indicate potential reporting, accounting, or structural risk."
            isCondensed={isCondensed}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtTotalReconciliationDiffP",
                        yName: "Debt Total Reconciliation Diff %",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const percentValue =
                                    datum.debtTotalReconciliationDiffP;

                                return {
                                    heading: `${month}`,
                                    title: "Debt Total Reconciliation Diff %",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt Total Reconciliation Diff %",
                                            value: `${(percentValue * 100).toFixed(0)}%`,
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtServiceReconcilitationDiffP",
                        yName: "Debt Service Reconciliation Diff %",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const percentValue =
                                    datum.debtServiceReconcilitationDiffP;

                                return {
                                    heading: `${month}`,
                                    title: "Debt Service Reconciliation Diff %",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt Service Reconciliation Diff %",
                                            value: `${(percentValue * 100).toFixed(0)}%`,
                                        },
                                        {
                                            label: "Interest Expense",
                                            value: formatCurrency(
                                                datum.interestExpense,
                                            ),
                                        },
                                        {
                                            label: "Interest and Fees",
                                            value: formatCurrency(
                                                datum.interestAndFees,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        type: "number",
                        position: "right",
                        label: {
                            formatter: (params) => {
                                return `${(params.value * 100).toFixed(0)}%`;
                            },
                        },
                        keys: [
                            "debtTotalReconciliationDiffP",
                            "debtServiceReconcilitationDiffP",
                        ],
                    },
                ],
            }}
        />
    );
}
